export type AssistantGoogleWorkspaceArtifactState =
  | "ready"
  | "exportable"
  | "connect_required"
  | "download_only";

export type AssistantGoogleWorkspaceBridgeStatus = {
  connected: boolean;
  bridgeState?: string | null;
  supportedEnvironment?: boolean | null;
  statusLabel?: string | null;
  connectHref?: string | null;
};

export type AssistantGoogleWorkspaceArtifactAction = {
  kind: "google_sheets";
  state: AssistantGoogleWorkspaceArtifactState;
  fileUrl?: string | null;
  workbookArtifactPath?: string | null;
  artifactDownloadUrl?: string | null;
  mimeType?: string | null;
  fileName?: string | null;
  statusLabel?: string | null;
  connectHref?: string | null;
};

export type AssistantGoogleWorkspaceArtifactInput = {
  id?: string | null;
  label?: string | null;
  kind?: string | null;
  badge?: string | null;
  meta?: string | null;
  href?: string | null;
  sourceRef?: string | null;
  downloadUrl?: string | null;
  artifactDownloadUrl?: string | null;
  workbookArtifactPath?: string | null;
  mimeType?: string | null;
  fileName?: string | null;
  googleWorkspace?: AssistantGoogleWorkspaceArtifactAction | null;
};

export type AssistantGoogleSheetsExportInput = {
  fileName: string;
  workbookArtifactPath?: string | null;
  artifactDownloadUrl?: string | null;
  mimeType?: string | null;
  route?: string | null;
  reportRef?: string | null;
  sourceRef?: string | null;
};

export type AssistantGoogleWorkspaceActionOptions = {
  fallbackFileExtension?: "csv" | "xls" | "xlsx";
  syntheticWorkbookPathPatterns?: RegExp[];
};

const DEFAULT_SYNTHETIC_WORKBOOK_PATH_PATTERNS = [
  /(^|\/)assistant-artifacts\/[^/]+\/workbook\.json$/i,
];

const readText = (...values: unknown[]): string => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) {
      return text;
    }
  }
  return "";
};

export const isBlankGoogleSheetsUrl = (value: unknown): boolean => {
  try {
    const parsed = new URL(readText(value));
    return (
      parsed.protocol === "https:" &&
      parsed.hostname.toLowerCase() === "sheets.new" &&
      parsed.pathname.replace(/\/+$/, "") === ""
    );
  } catch {
    return false;
  }
};

export const isAssistantSpreadsheetArtifactSignature = (
  signature: string,
): boolean => /\b(csv|xls|xlsx|excel|spreadsheet|workbook)\b/i.test(signature);

export const isAssistantSpreadsheetArtifact = (
  item: AssistantGoogleWorkspaceArtifactInput,
): boolean => {
  const signature = [
    item.kind,
    item.badge,
    item.label,
    item.meta,
    item.fileName,
    item.mimeType,
    item.href,
    item.downloadUrl,
    item.artifactDownloadUrl,
    item.googleWorkspace?.mimeType,
    item.googleWorkspace?.fileName,
  ].filter(Boolean).join(" ");
  return isAssistantSpreadsheetArtifactSignature(signature);
};

const safeArtifactPathSegment = (value: string): string => {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return normalized || "assistant-artifact";
};

export const inferAssistantSpreadsheetArtifactMimeType = (
  item: AssistantGoogleWorkspaceArtifactInput,
): string => {
  const signature = [
    item.kind,
    item.badge,
    item.label,
    item.meta,
    item.fileName,
    item.mimeType,
    item.href,
    item.downloadUrl,
    item.artifactDownloadUrl,
    item.googleWorkspace?.mimeType,
    item.googleWorkspace?.fileName,
  ].filter(Boolean).join(" ");
  if (/\b(csv)\b|text\/csv/i.test(signature)) {
    return "text/csv";
  }
  if (
    /\b(xls|xlsx|excel|spreadsheet|workbook)\b|application\/vnd\.ms-excel|spreadsheetml/i.test(signature)
  ) {
    return "application/vnd.ms-excel";
  }
  return "";
};

const resolveWorkbookArtifactPath = (
  item: AssistantGoogleWorkspaceArtifactInput,
  options: AssistantGoogleWorkspaceActionOptions = {},
): string => {
  const workbookArtifactPath = readText(
    item.googleWorkspace?.workbookArtifactPath,
    item.workbookArtifactPath,
    item.sourceRef,
  );
  if (!workbookArtifactPath) {
    return "";
  }
  const syntheticPatterns =
    options.syntheticWorkbookPathPatterns ??
    DEFAULT_SYNTHETIC_WORKBOOK_PATH_PATTERNS;
  if (syntheticPatterns.some((pattern) => pattern.test(workbookArtifactPath))) {
    return "";
  }
  return workbookArtifactPath;
};

const resolveArtifactDownloadUrl = (
  item: AssistantGoogleWorkspaceArtifactInput,
): string => readText(
  item.googleWorkspace?.artifactDownloadUrl,
  item.artifactDownloadUrl,
  item.href,
  item.downloadUrl,
);

const artifactDownloadUrlLooksJson = (value: string): boolean =>
  /^data:application\/json\b/i.test(value)
  || /\.json(?:[?#]|$)/i.test(value)
  || /\bapplication\/json\b/i.test(value);

const resolveArtifactFileName = (
  item: AssistantGoogleWorkspaceArtifactInput,
  options: AssistantGoogleWorkspaceActionOptions = {},
): string => {
  const extension = options.fallbackFileExtension ?? "xlsx";
  return readText(
    item.googleWorkspace?.fileName,
    item.fileName,
    item.meta,
    `${safeArtifactPathSegment(readText(item.id, item.label, "assistant-artifact"))}.${extension}`,
  );
};

export const buildAssistantGoogleWorkspaceAction = (
  item: AssistantGoogleWorkspaceArtifactInput,
  bridge: AssistantGoogleWorkspaceBridgeStatus,
  options: AssistantGoogleWorkspaceActionOptions = {},
): AssistantGoogleWorkspaceArtifactAction | null => {
  if (!isAssistantSpreadsheetArtifact(item)) {
    return null;
  }

  const existingFileUrl = readText(item.googleWorkspace?.fileUrl);
  const readyFileUrl =
    existingFileUrl && !isBlankGoogleSheetsUrl(existingFileUrl)
      ? existingFileUrl
      : "";
  const workbookArtifactPath = resolveWorkbookArtifactPath(item, options);
  const artifactDownloadUrl = resolveArtifactDownloadUrl(item);
  const mimeType = readText(
    item.googleWorkspace?.mimeType,
    item.mimeType,
    inferAssistantSpreadsheetArtifactMimeType(item),
  );
  const fileName = resolveArtifactFileName(item, options);
  const hasArtifactSource = Boolean(workbookArtifactPath || artifactDownloadUrl);
  const supportedEnvironment = bridge.supportedEnvironment !== false;
  const connectHref = readText(bridge.connectHref, item.googleWorkspace?.connectHref);
  const state: AssistantGoogleWorkspaceArtifactState = readyFileUrl
    ? "ready"
    : supportedEnvironment && hasArtifactSource
      ? "exportable"
      : !bridge.connected && supportedEnvironment && connectHref
        ? "connect_required"
        : "download_only";

  return {
    kind: "google_sheets",
    state,
    fileUrl: readyFileUrl || null,
    workbookArtifactPath: workbookArtifactPath || null,
    artifactDownloadUrl: artifactDownloadUrl || null,
    mimeType: mimeType || null,
    fileName,
    statusLabel:
      state === "download_only"
        ? bridge.statusLabel ||
          "Direct downloads are available for this generated artifact."
        : bridge.statusLabel ?? null,
    connectHref: state === "connect_required" ? connectHref : null,
  };
};

export const buildAssistantGoogleSheetsExportInput = (
  item: AssistantGoogleWorkspaceArtifactInput,
  options: AssistantGoogleWorkspaceActionOptions = {},
): AssistantGoogleSheetsExportInput => {
  const fileName = resolveArtifactFileName(item, options);
  const workbookArtifactPath = resolveWorkbookArtifactPath(item, options);
  const artifactDownloadUrl = resolveArtifactDownloadUrl(item);
  const mimeType = readText(
    item.googleWorkspace?.mimeType,
    item.mimeType,
    inferAssistantSpreadsheetArtifactMimeType(item),
  );

  if (!fileName) {
    throw new Error("Google Sheets export requires an artifact file name.");
  }
  if (!workbookArtifactPath && !artifactDownloadUrl) {
    throw new Error(
      "Google Sheets export requires a workbook artifact path or artifact download URL.",
    );
  }
  if (/\.csv$/i.test(fileName) && artifactDownloadUrl && artifactDownloadUrlLooksJson(artifactDownloadUrl)) {
    throw new Error(
      "CSV Google Sheets export requires a CSV artifact download URL, not a JSON artifact URL.",
    );
  }

  return {
    fileName,
    workbookArtifactPath: workbookArtifactPath || null,
    artifactDownloadUrl: artifactDownloadUrl || null,
    mimeType: mimeType || null,
    route: null,
    reportRef: null,
    sourceRef: readText(item.sourceRef, item.id) || null,
  };
};
